import _, { isNull } from 'lodash';
import 'moment';
import 'moment-timezone';

import { Get, urls } from 'assets/js/api';
import { MomentTz } from 'assets/js/time';

export const ACCOUNT_APPLIED_TYPES = {
  RESERVABLE_SITE: 'account_applied_type_reservable_site',
  USER_PORTAL: 'account_applied_type_user_portal',
  USER_SITE: 'account_applied_type_user_site',
};

export const ACCOUNT_INTEGER_TO_FLOAT_DIV = 100;

export const PORTAL_USER_ACCOUNT_NAMESPACES = {
  USER_FUNDS: 'portal_user_funds',
};

export const SITE_RESERVABLE_ACCOUNT_NAMESPACES = {
  UNITS: 'site_reservable_units',
};

export const SITE_USER_ACCOUNT_NAMESPACES = {
  USER_FUNDS: 'site_user_funds',
};

const dtFormat = 'ddd, MMM Do YYYY, h:mm A';

export const HasTransactionError = Error('The new start or end time ' +
  'conflicts with the transactions in the account. Please inactivate ' +
  'either the account or transactions before/after this time to make ' +
  'this modification.');
export const OverlappingError = Error('Overlapping accounts require ' +
  'modification before new account can be added');

export const PythonMinTime = '1-01-01T00:00:00.000Z';
export const PythonMaxTime = '9999-01-01T00:00:00.000Z';

// generateModUserAccount generates POST parameters for a site
// user account modification.
const generateModUserAccount = ({
  accountId,
  appliedAccountId,
  modification,
  portalId = null,
  siteId = null,
  userId,
}) => _.pickBy(Object({
  action: 'modify',
  data: {
    account_applied_id: appliedAccountId,
    account_id: accountId,
    ...modification,
  },
  return_data: false,
  portal_id: portalId,
  site_id: siteId,
  user_id: userId,
}, (val) => !(_.isNull(val) || _.isUndefined(val))));

// FetchAccounts is a fetch function that allows access to all the variables
// for the GET account fetch API.
export const FetchAccounts = async ({
  accountType = null,
  childrenEnd = null,
  childrenStart = null,
  limit = null,
  missing = null,
  missingWhen = null,
  offset = null,
  search = null,
  siteId = null,
  portalId = null,
  sortBy = 'nameasc',
  userId = null,
  verbose = false,
  when = null,
}) => {
  if (_.isNull(accountType)) {
    throw Error('FetchAccounts: accountType is missing');
  }
  if ((_.isNull(portalId) && _.isNull(userId)) &&
    (_.isNull(siteId) && _.isNull(userId))) {
    throw Error('FetchAccounts: siteId and portalId plus userId must be ' +
      'supplied');
  }

  const url = urls.ACCOUNTS;
  const resp = await Get(url, {
    params:
      _.pickBy({
        account_type: accountType,
        children_end: childrenEnd,
        children_start: childrenStart,
        include_missing: missing,
        limit,
        missing_when: missingWhen,
        offset,
        portal_id: portalId,
        return_count: true,
        search,
        site_id: siteId,
        sort_mtd: sortBy,
        user_id: userId,
        verbose,
        when,
      }, (val) => !(_.isNull(val) || _.isUndefined(val))),
  });
  return resp;
};

// CheckAndFixAccountConflicts handles ensuring that account state
// transitions when creating a new account are allowed. It prompts the
// user to alter or change old accounts which may be contradictory
// to the new account.
//
// When extendFrom and extendTo are set, this function will search for an
// account to extend and then generate a new schema for that account.
// Once complete, it will return that account and modified schema for the
// API to operate on.
export const CheckAndFixAccountConflicts = async ({
  accountType = ACCOUNT_APPLIED_TYPES.USER_SITE,
  appliedAccountsArg = null,
  automaticConflictFixing = false,
  createModifyAccountsFn,
  ctx = this,
  modifiers = {
    from: null, // unix timestamp
    extendTo: null, // unix timestamp
    truncateTo: null, // unix timestamp
  },
  modifyingId = null,
  nameFn,
  objWord,
  portalId = null,
  schemaIncoming,
  siteId = null,
  testing = false,
  timezone = 'UTC',
  userId = null,
}) => {
  if (_.isNull(userId)) throw Error('No user ID given');
  if (_.isNull(siteId) && _.isNull(portalId)) {
    throw Error('No site or portal ID given');
  }
  if (!_.isNull(modifiers.extendTo) && !_.isNull(modifiers.truncateTo)) {
    throw Error('Impossible to simultaneously extend and truncate accounts');
  }

  let appliedAccounts = appliedAccountsArg;
  if (_.isNull(appliedAccounts)) {
    try {
      const resp = await FetchAccounts(_.pickBy({
        accountType,
        portalId,
        siteId,
        userId,
      }, (val) => !(_.isNull(val) || _.isUndefined(val))));
      appliedAccounts = resp.data;
    } catch (err) {
      if (!testing) {
        ctx.updateLogMessages.push({
          status: 'error',
          err: err.message,
          message: `Failed to fetch accounts for ${objWord} ` +
            `ID '${userId}''; skipping)`,
        });
      } else {
        await ctx.$swal({
          title: 'Unable to retrieve accounts',
          text: `The accounts for user ID ${userId} ` +
            'were unable to be retrieved.',
          type: 'error',
          allowOutsideClick: false,
          allowEscapeKey: true,
          showCancelButton: false,
          confirmButtonText: 'OK',
        });
      }
      return true;
    }
  }

  const applied = _.find(appliedAccounts, (acct) => acct.user_id === userId);

  // The user has no conflicting accounts, so just return.
  if (_.isUndefined(applied)) {
    ctx.updateLogMessages.push({
      status: 'info',
      message: 'No accounts found, continuing',
      err: null,
    });
    return true;
  }

  ctx.updateLogMessages.push({
    status: 'info',
    message: 'Starting account conflict resolver for ' +
      `${objWord} ${nameFn(applied)}`,
    err: null,
  });

  let schema = schemaIncoming;
  let accountModifying = _.find(applied.accounts, (acct) =>
    acct.account_id === modifyingId);
  const noStartTime = -1;
  const noEndTime = Number.MAX_SAFE_INTEGER;

  // We're extending an account, so we need to fix an account that is valid
  // for the extendFrom time then manipulate its schema so that it is extended.
  if (!_.isNull(modifiers.from) && (!_.isNull(modifiers.extendTo) ||
    !isNull(modifiers.truncateTo))) {
    accountModifying = _.find(_.get(applied, 'accounts'), (acct) => {
      if (!acct.active) return false;
      const acctSchema = acct.schema.current;
      const acctStart = acctSchema.has_start ? acctSchema.time_start :
        noStartTime;
      const acctEnd = acctSchema.has_end ? acctSchema.time_end :
        noEndTime;
      return acctStart <= modifiers.from && modifiers.from < acctEnd;
    });
    if (!accountModifying) {
      ctx.updateLogMessages.push({
        status: 'info',
        message: 'No accounts to modify could be found for ' +
          `${objWord} ${nameFn(applied)}; skipping update for this ${objWord}`,
        err: null,
      });
      return true;
    }

    schema = _.cloneDeep(accountModifying.schema.current);

    // Handle extensions.
    if (!_.isNull(modifiers.extendTo) && (
      !schema.has_end ||
      (schema.time_end && schema.time_end > modifiers.extendTo)
    )) {
      ctx.updateLogMessages.push({
        status: 'info',
        message: 'No extension is possible for the active account for ' +
          `${objWord} ${nameFn(applied)} because the account has no end ` +
          'period or the current end period is after the time to extend ' +
          'the account to',
        err: null,
      });
      return true;
    }
    if (!_.isNull(modifiers.extendTo)) {
      schema.time_end = modifiers.extendTo;
    }

    // Handle truncations.
    if (!_.isNull(modifiers.truncateTo) &&
      schema.time_end && schema.time_end < modifiers.truncateTo
    ) {
      ctx.updateLogMessages.push({
        status: 'info',
        message: 'No truncation is possible for the active account for ' +
          `${objWord} ${nameFn(applied)} because the account has an end ` +
          'period less than the intended time to truncate to',
        err: null,
      });
      return true;
    }
    if (!_.isNull(modifiers.truncateTo)) {
      schema.has_end = true;
      schema.time_end = modifiers.truncateTo;
    }
  }

  const toInactivate = [];
  const toEndEarly = [];
  const toStartLate = [];

  // The only thing we care about is determining a list of accounts that
  // conflict in some way and modifying them so that they no longer
  // conflict. We can just loop over the accounts and determine from
  // their schema times whether or not this is the case.
  //
  // Keep in mind that accounts are [start, end).
  _.forEach(applied.accounts, (acct) => {
    const timeStart = schema.has_start ? schema.time_start : noStartTime;
    const timeEnd = schema.has_end ? schema.time_end : noEndTime;
    const timeStartOld = _.get(acct, 'schema.current.time_start', noStartTime);
    const timeEndOld = _.get(acct, 'schema.current.time_end', noEndTime);

    // Accounts are already sorted by when they occur.
    let txTimeFirst = null;
    let txTimeLast = null;
    if (_.get(acct, 'children.length') > 0) {
      // Get the time of the first transaction.
      let txTimeFirstStr = null;
      _.forEach(acct.children, (child) => {
        if (!_.isNull(child.tx_time_first)) {
          txTimeFirstStr = child.tx_time_first;
          return false;
        }
        return true;
      });
      if (!_.isNull(txTimeFirstStr)) {
        txTimeFirst = MomentTz(txTimeFirstStr, 'UTC').unix();
      }

      // Get the time of the last transaction.
      let txTimeLastStr = null;
      _.forEachRight(acct.children, (child) => {
        if (!_.isNull(child.tx_time_first)) {
          txTimeLastStr = child.tx_time_last;
          return false;
        }
        return true;
      });
      if (!_.isNull(txTimeLastStr)) {
        txTimeLast = MomentTz(txTimeLastStr, 'UTC').unix();
      }
    }

    if (!_.isNull(txTimeFirst) && timeStart > txTimeFirst) {
      ctx.updateLogMessages.push({
        status: 'error',
        message: HasTransactionError.message,
        err: HasTransactionError,
      });
      throw HasTransactionError;
    }
    if (!_.isNull(txTimeLast) && timeEnd <= txTimeLast) {
      ctx.updateLogMessages.push({
        status: 'error',
        message: HasTransactionError.message,
        err: HasTransactionError,
      });
      throw HasTransactionError;
    }

    // Return if this is the account we're modifying.
    if (!_.isUndefined(accountModifying) &&
      accountModifying.account_id === acct.account_id) {
      return true;
    }

    // New schema has no start and no end --> inactivate the old account.
    if (!schema.has_start && !schema.time_end) {
      toInactivate.push(acct);
      return true;
    }

    // Detect overlap.
    if (timeStartOld < timeEnd && timeEndOld > timeStart) {
      // Check and see if we can end this old account early and
      // an overlap won't exist. Note that we can't end the account
      // early if it results in the account having the same end time
      // as the start time.
      const timeEndOldEndedEarly = timeStart;
      if ((timeEndOldEndedEarly > timeStartOld) &&
        !(timeStartOld < timeEnd && timeEndOldEndedEarly > timeStart)) {
        toEndEarly.push(acct);
        return true;
      }

      // Check and see if we can start this old account later and
      // an overlap won't exist.
      const timeStartOldStartLater = timeEnd;
      if ((timeStartOldStartLater < timeEndOld) &&
        !(timeStartOldStartLater < timeEnd && timeEndOld > timeStart)) {
        toStartLate.push(acct);
        return true;
      }

      // No tweak is going to work, so just prompt the user to inactivate it.
      // This is a fallthrough condition and generally should only be hit
      // if the accounts exactly overlap.
      toInactivate.push(acct);
      return true;
    }

    return true;
  });

  if (testing) {
    return {
      toInactivate,
      toEndEarly,
      toStartLate,
    };
  }

  const accountDescription = (acct) => {
    const timeStartStr = MomentTz(
      _.get(acct, 'schema.current.time_start', 0),
      'UTC',
      true,
    ).tz(timezone).format(dtFormat);
    const timeEndStr = MomentTz(
      _.get(acct, 'schema.current.time_end', 0),
      'UTC',
      true,
    ).tz(timezone).format(dtFormat);
    if (!acct.schema.current.has_start && !acct.schema.current.has_end) {
      return 'with no start or end';
    }
    if (!acct.schema.current.has_start && acct.schema.current.has_end) {
      return `with no start date and an end date of ${timeEndStr}`;
    }
    if (acct.schema.current.has_start && !acct.schema.current.has_end) {
      return `with a start date of ${timeStartStr} and no end date`;
    }
    return `with a start date of ${timeStartStr} and end date of ${timeEndStr}`;
  };

  const handleErr = (errLocal, todo) => ctx.updateLogMessages.push({
    status: 'error',
    err: errLocal.message,
    message: 'Error while attempting to fix account conflict for user ' +
      `${nameFn(applied)} (${accountDescription(todo)})`,
  });

  // Prompt the user to modify the old accounts with sweet alert. Any
  // cancellation should raise an error here.
  await ctx.asyncForEach(toInactivate, async (todo) => {
    ctx.updateLogMessages.push({
      status: 'info',
      message: `Deactivating conflicting account ${accountDescription(todo)} ` +
        `for ${objWord} ${nameFn(applied)}`,
      err: null,
    });

    const params = generateModUserAccount({
      accountId: todo.account_id,
      appliedAccountId: applied.account_applied_id,
      modification: {
        active: false,
      },
      portalId: applied.portal_id,
      siteId: applied.site_id,
      userId,
    });

    if (!automaticConflictFixing) {
      try {
        await ctx.$swal({
          title:
            `${ctx.capitalizeString(objWord)} Account Requires Inactivation`,
          text: 'In order to create the new account you have requested for ' +
            `${nameFn(applied)}, you will have to first deactivate a ` +
            `fully overlapping account ${accountDescription(todo)}.`,
          type: 'warning',
          showLoaderOnConfirm: true,
          preConfirm: async () => {
            try {
              await createModifyAccountsFn(params);
            } catch (err) {
              handleErr(err, todo);
            }

            return true;
          },
          allowOutsideClick: false,
          allowEscapeKey: false,
          showCancelButton: true,
          confirmButtonText: 'Deactivate',
          cancelButtonText: 'Cancel',
        });
      } catch (err) {
        // sweetalert cancel
        throw OverlappingError;
      }
    } else {
      try {
        await createModifyAccountsFn(params);
      } catch (err) {
        handleErr(err);
        return true;
      }
    }

    ctx.updateLogMessages.push({
      status: 'success',
      message: `Conflicting account for ${objWord} ${nameFn(applied)} has ` +
        'been deactivated',
      err: null,
    });

    return true;
  });

  await ctx.asyncForEach(toEndEarly, async (todo) => {
    ctx.updateLogMessages.push({
      status: 'info',
      message: `Shortening conflicting account ${accountDescription(todo)} ` +
        `for ${objWord} ${nameFn(applied)}`,
      err: null,
    });

    const params = generateModUserAccount({
      accountId: todo.account_id,
      appliedAccountId: applied.account_applied_id,
      modification: {
        time_end: schema.time_start,
      },
      portalId: applied.portal_id,
      siteId: applied.site_id,
      userId,
    });

    if (!automaticConflictFixing) {
      try {
        await ctx.$swal({
          title: `${ctx.capitalizeString(objWord)} Account Requires End ` +
            'Date Adjustment',
          text: 'In order to create the new account you have requested for ' +
            `${nameFn(applied)}, you will have to first modify the end date ` +
            `for the overlapping account ${accountDescription(todo)}.`,
          type: 'warning',
          showLoaderOnConfirm: true,
          preConfirm: async () => {
            try {
              await createModifyAccountsFn(params);
            } catch (err) {
              handleErr(err);
            }
            return true;
          },
          allowOutsideClick: false,
          allowEscapeKey: false,
          showCancelButton: true,
          confirmButtonText: 'End Early',
          cancelButtonText: 'Cancel',
        });
      } catch (err) {
        // sweetalert cancel
        throw OverlappingError;
      }
    } else {
      try {
        await createModifyAccountsFn(params);
      } catch (err) {
        handleErr(err);
        return true;
      }
    }

    ctx.updateLogMessages.push({
      status: 'success',
      message: `Conflicting account for ${objWord} ${nameFn(applied)} ` +
        'has been shortened to accommodate new account',
      err: null,
    });
    return true;
  });

  await ctx.asyncForEach(toStartLate, async (todo) => {
    ctx.updateLogMessages.push({
      status: 'info',
      message: `Shortening conflicting account ${accountDescription(todo)} ` +
        `for ${objWord} ${nameFn(applied)}`,
      err: null,
    });

    const params = generateModUserAccount({
      accountId: todo.account_id,
      appliedAccountId: applied.account_applied_id,
      modification: {
        time_start: schema.time_end,
      },
      portalId: applied.portal_id,
      siteId: applied.site_id,
      userId,
    });

    if (!automaticConflictFixing) {
      try {
        await ctx.$swal({
          title: `${ctx.capitalizeString(objWord)} Account Requires ` +
            'Start Date Adjustment',
          text: 'In order to create the new account you have requested for ' +
            `${nameFn(applied)}, you will have to first set the state date ` +
            `later for the overlapping account ${accountDescription(todo)}.`,
          type: 'warning',
          showLoaderOnConfirm: true,
          preConfirm: async () => {
            try {
              await createModifyAccountsFn(params);
            } catch (err) {
              handleErr(err);
            }

            return true;
          },
          allowOutsideClick: false,
          allowEscapeKey: false,
          showCancelButton: true,
          confirmButtonText: 'Start Later',
          cancelButtonText: 'Cancel',
        });
      } catch (err) {
        // sweetalert cancel
        throw OverlappingError;
      }
    } else {
      try {
        await createModifyAccountsFn(params);
      } catch (err) {
        handleErr(err);
      }
    }

    ctx.updateLogMessages.push({
      status: 'success',
      message: `Conflicting account for ${objWord} ${nameFn(applied)} ` +
        'has been shortened to accommodate new account',
      err: null,
    });
    return true;
  });

  ctx.updateLogMessages.push({
    status: 'success',
    message: `Account conflict resolver for ${objWord} ${nameFn(applied)} ` +
      'successfully ran',
    err: null,
  });

  if (!_.isNull(modifiers.from) && (!_.isNull(modifiers.extendTo) ||
    !isNull(modifiers.truncateTo))) {
    return {
      account: accountModifying,
      applied,
      schema,
    };
  }

  return true;
};
